const PRODUCT_PAGE_SELECTOR = '.product-information[data-testid="product-information"]';
const PRODUCT_RECOMMENDATIONS_SELECTOR = 'product-recommendations';
const PRODUCT_DESKTOP_QUERY = '(min-width: 750px)';
const RECOMMENDATION_IMAGE_PREVIEW_WIDTH = 900;
const RECOMMENDATION_IMAGE_FULL_WIDTH = 1800;
const PRODUCT_IMAGE_PREVIEW_WIDTH = 1200;
const PRODUCT_IMAGE_FULL_WIDTH = 2200;
const productDesktopMedia = window.matchMedia(PRODUCT_DESKTOP_QUERY);
const productHeightSyncQueue = new Set();
const productQuickViewCache = new Map();
const modalImagePreloadCache = new Map();
let productHeightSyncFrame = 0;
let modalImageSourceRequestId = 0;
let productRecommendationsObserver = null;
let recommendationPrefetchObserver = null;
let recommendationQuickViewBound = false;
let productImageModalBound = false;
let productHelpDrawerBound = false;

const recommendationQuickViewState = {
  modal: null,
  activeProduct: null,
  activeImageIndex: 0,
  lastFocusedElement: null,
  scrollY: 0,
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverflow: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
  swipe: null,
};

const productImageModalState = {
  modal: null,
  images: [],
  activeImageIndex: 0,
  lastFocusedElement: null,
  scrollY: 0,
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverflow: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
  swipe: null,
};

const productHelpDrawerState = {
  modal: null,
  lastFocusedElement: null,
  scrollY: 0,
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverflow: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
};

function bindDescriptionScrollRelease(description) {
  if (!(description instanceof HTMLElement) || description.dataset.hpScrollReleaseBound === 'true') return;

  description.dataset.hpScrollReleaseBound = 'true';
  let lastY = 0;
  const shouldReleaseToPage = (deltaY) => {
    if (Math.abs(deltaY) < 1) return false;
    if (description.scrollHeight <= description.clientHeight + 1) return false;

    const atTop = description.scrollTop <= 1;
    const atBottom = description.scrollTop + description.clientHeight >= description.scrollHeight - 1;
    return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
  };

  description.addEventListener(
    'touchstart',
    (event) => {
      lastY = event.touches?.[0]?.clientY || 0;
    },
    { passive: true }
  );

  description.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaY = lastY - touch.clientY;
      lastY = touch.clientY;

      if (!shouldReleaseToPage(deltaY)) return;

      event.preventDefault();
      window.scrollBy(0, deltaY);
    },
    { passive: false }
  );

  description.addEventListener(
    'wheel',
    (event) => {
      if (!shouldReleaseToPage(event.deltaY)) return;

      event.preventDefault();
      window.scrollBy({ top: event.deltaY, left: 0, behavior: 'auto' });
    },
    { passive: false }
  );
}

function bindGalleryEdgeGuard(scroller) {
  if (!(scroller instanceof HTMLElement) || scroller.dataset.hpEdgeGuardBound === 'true') return;

  scroller.dataset.hpEdgeGuardBound = 'true';
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let isHorizontalSwipe = false;
  let isVerticalSwipe = false;
  let snapTimer = 0;

  const getMaxScrollLeft = () => Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
  const getSlideWidth = () => Math.max(scroller.clientWidth, 1);
  const snapToNearestSlide = () => {
    window.clearTimeout(snapTimer);
    const slideWidth = getSlideWidth();
    const maxScrollLeft = getMaxScrollLeft();
    if (!maxScrollLeft) {
      scroller.scrollLeft = 0;
      return;
    }
    const index = Math.round(scroller.scrollLeft / slideWidth);
    const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, index * slideWidth));
    if (Math.abs(scroller.scrollLeft - nextScrollLeft) <= 1) return;
    scroller.scrollTo({ left: nextScrollLeft, behavior: 'auto' });
  };
  const scheduleSnap = (delay = 120) => {
    window.clearTimeout(snapTimer);
    snapTimer = window.setTimeout(snapToNearestSlide, delay);
  };

  scroller.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;

      window.clearTimeout(snapTimer);
      startX = touch.clientX;
      startY = touch.clientY;
      startScrollLeft = scroller.scrollLeft;
      isHorizontalSwipe = false;
      isVerticalSwipe = false;
    },
    { passive: true }
  );

  scroller.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      if (!isHorizontalSwipe && !isVerticalSwipe && Math.abs(deltaY) > 10 && Math.abs(deltaY) > Math.abs(deltaX) * 1.15) {
        isVerticalSwipe = true;
      }

      if (!isHorizontalSwipe && !isVerticalSwipe && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
        isHorizontalSwipe = true;
      }

      if (isVerticalSwipe) {
        if (Math.abs(scroller.scrollLeft - startScrollLeft) > 1) scroller.scrollLeft = startScrollLeft;
        scheduleSnap(80);
        return;
      }

      if (!isHorizontalSwipe) return;

      const maxScrollLeft = getMaxScrollLeft();
      const atStart = scroller.scrollLeft <= 1;
      const atEnd = scroller.scrollLeft >= maxScrollLeft - 1;
      const pullingBeforeFirst = atStart && deltaX > 0;
      const pullingAfterLast = atEnd && deltaX < 0;

      if (!pullingBeforeFirst && !pullingAfterLast) return;

      event.preventDefault();
      scroller.scrollLeft = pullingBeforeFirst ? 0 : maxScrollLeft;
    },
    { passive: false }
  );

  scroller.addEventListener('touchend', () => scheduleSnap(40), { passive: true });
  scroller.addEventListener('touchcancel', () => scheduleSnap(40), { passive: true });
  scroller.addEventListener('scroll', () => scheduleSnap(150), { passive: true });
}

function enhanceProductGalleryLayout(productInfo) {
  const containers = Array.from(
    productInfo.querySelectorAll('.product-information__media .media-gallery__grid .product-media-container--image')
  ).filter((container) => container instanceof HTMLElement);

  containers.forEach((container) => {
    container.classList.remove('hp-product-gallery__more');
    container.removeAttribute('data-hp-gallery-more-label');
  });

  const hiddenCount = Math.max(containers.length - 5, 0);
  if (hiddenCount <= 0) return;

  const moreContainer = containers[4];
  moreContainer.classList.add('hp-product-gallery__more');
  moreContainer.setAttribute('data-hp-gallery-more-label', `+${hiddenCount} MORE`);
}

function bindProductGalleryLayoutEnhancements(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpGalleryLayoutBound === 'true') return;

  productInfo.dataset.hpGalleryLayoutBound = 'true';
  enhanceProductGalleryLayout(productInfo);

  if (typeof MutationObserver !== 'function') return;

  const observer = new MutationObserver(() => {
    enhanceProductGalleryLayout(productInfo);
    scheduleProductDetailHeightSync(productInfo);
  });
  observer.observe(productInfo, { childList: true, subtree: true });
  productInfo.hpGalleryLayoutObserver = observer;
}

function bindProductGalleryMobileArrows(productInfo) {
  if (!(productInfo instanceof HTMLElement)) return;

  productInfo.querySelectorAll('.product-information__media media-gallery').forEach((gallery) => {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.hpMobileArrowsBound === 'true') return;

    const scroller = gallery.querySelector('slideshow-slides');
    const container = gallery.querySelector('slideshow-container');
    if (!(scroller instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    const slideCount = Array.from(scroller.children).filter((child) =>
      child instanceof HTMLElement && child.matches('slideshow-slide, .product-media-container')
    ).length;
    if (slideCount <= 1) return;

    gallery.dataset.hpMobileArrowsBound = 'true';
    const previous = createProductGalleryArrow('prev');
    const next = createProductGalleryArrow('next');
    container.append(previous, next);

    const update = () => updateProductGalleryArrowState(scroller, previous, next, gallery);
    const move = (direction, event) => {
      const slideWidth = Math.max(scroller.clientWidth, 1);
      const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      const maxIndex = Math.max(Math.round(maxScrollLeft / slideWidth), 0);
      const currentIndex = getProductGalleryArrowIndex(scroller, gallery);
      const nextIndex = Math.max(0, Math.min(maxIndex, currentIndex + direction));
      const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, nextIndex * slideWidth));
      gallery.dataset.hpMobileGalleryIndex = String(nextIndex);
      gallery.dataset.hpMobileGalleryLockUntil = String(Date.now() + 1400);
      previous.disabled = nextIndex <= 0;
      next.disabled = nextIndex >= maxIndex;
      scroller.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });

      window.setTimeout(update, 120);
      window.setTimeout(update, 1480);
    };

    previous.addEventListener('click', (event) => {
      event.preventDefault();
      move(-1, event);
    });
    next.addEventListener('click', (event) => {
      event.preventDefault();
      move(1, event);
    });
    scroller.addEventListener('scroll', update, { passive: true });
    slideshow?.addEventListener?.('slideshow:select', update);
    window.addEventListener('resize', update, { passive: true });
    update();
    window.requestAnimationFrame(update);
    window.setTimeout(update, 180);
  });
}

function createProductGalleryArrow(direction) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `hp-product-gallery-arrow hp-product-gallery-arrow--${direction}`;
  button.setAttribute('aria-label', direction === 'next' ? 'Next product image' : 'Previous product image');
  button.innerHTML = direction === 'next'
    ? '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>'
    : '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>';
  return button;
}

function getProductGalleryArrowIndex(scroller, gallery) {
  const slideWidth = Math.max(scroller.clientWidth, 1);
  const scrollIndex = Math.round(scroller.scrollLeft / slideWidth);
  const storedIndex = Number.parseInt(gallery?.dataset?.hpMobileGalleryIndex || '', 10);
  const lockUntil = Number.parseInt(gallery?.dataset?.hpMobileGalleryLockUntil || '', 10);
  if (Number.isFinite(storedIndex) && Number.isFinite(lockUntil) && Date.now() < lockUntil) return storedIndex;
  return scrollIndex;
}

function updateProductGalleryArrowState(scroller, previous, next, gallery) {
  const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
  const slideWidth = Math.max(scroller.clientWidth, 1);
  const maxIndex = Math.max(Math.round(maxScrollLeft / slideWidth), 0);
  const currentIndex = Math.max(0, Math.min(maxIndex, getProductGalleryArrowIndex(scroller, gallery)));
  if (gallery instanceof HTMLElement) gallery.dataset.hpMobileGalleryIndex = String(currentIndex);
  previous.disabled = currentIndex <= 0;
  next.disabled = currentIndex >= maxIndex;
}

function getProductGalleryPosition(productInfo) {
  const gallery = productInfo.querySelector('.product-information__media media-gallery');
  const scroller = gallery?.querySelector('slideshow-slides');
  const slideshow = gallery?.slideshow || gallery?.querySelector('slideshow-component');

  return {
    scroller: scroller instanceof HTMLElement ? scroller : null,
    slideshow,
    scrollLeft: scroller instanceof HTMLElement ? scroller.scrollLeft : 0,
    current: Number.isFinite(slideshow?.current) ? slideshow.current : null,
  };
}

function restoreProductGalleryPosition(position) {
  if (!position?.scroller && !position?.slideshow) return;

  const restore = () => {
    if (position.slideshow && Number.isFinite(position.current) && typeof position.slideshow.select === 'function') {
      position.slideshow.select(position.current, undefined, { animate: false });
    }
    if (position.scroller) position.scroller.scrollLeft = position.scrollLeft || 0;
  };

  window.requestAnimationFrame(restore);
  window.setTimeout(restore, 80);
  window.setTimeout(restore, 240);
}

function bindProductVariantGalleryStability(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpVariantGalleryStable === 'true') return;

  productInfo.dataset.hpVariantGalleryStable = 'true';
  let latestPosition = null;
  const capture = () => {
    latestPosition = getProductGalleryPosition(productInfo);
  };
  const restore = () => {
    if (latestPosition) restoreProductGalleryPosition(latestPosition);
    scheduleProductDetailHeightSync(productInfo);
  };

  productInfo.addEventListener('change', capture, true);
  productInfo.addEventListener('variant:selected', capture, true);
  productInfo.addEventListener('variant:update', restore, true);
}

function requestProductIdleCallback(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 1200 });
    return;
  }

  window.setTimeout(callback, 120);
}

function setImageFetchPriority(image, priority = 'auto') {
  if (!(image instanceof HTMLImageElement)) return;

  if ('fetchPriority' in image) image.fetchPriority = priority;
  else image.setAttribute('fetchpriority', priority);
}

function preloadImageSrc(src, priority = 'auto') {
  const normalizedSrc = src ? String(src) : '';
  if (!normalizedSrc) return Promise.resolve();
  if (modalImagePreloadCache.has(normalizedSrc)) return modalImagePreloadCache.get(normalizedSrc);

  const image = new Image();
  image.decoding = 'async';
  setImageFetchPriority(image, priority);

  let resolveReady;
  const ready = new Promise((resolve) => {
    resolveReady = resolve;
    image.onload = resolve;
    image.onerror = resolve;
  });

  image.src = normalizedSrc;
  if (image.complete) resolveReady();

  const decoded = ready
    .then(() => {
      if (typeof image.decode !== 'function') return undefined;
      return image.decode().catch(() => undefined);
    })
    .catch(() => undefined);

  modalImagePreloadCache.set(normalizedSrc, decoded);
  return decoded;
}

function getSizedShopifyImageSrc(src, width) {
  const normalizedSrc = normalizeRecommendationImageSrc(src);
  if (!normalizedSrc || !width) return normalizedSrc;

  try {
    const url = new URL(normalizedSrc, window.location.origin);
    if (url.hostname.includes('cdn.shopify.com') || url.pathname.includes('/cdn/shop/')) {
      url.searchParams.set('width', String(width));
      return url.toString();
    }
  } catch (error) {
    return normalizedSrc;
  }

  return normalizedSrc;
}

function setModalImageSource(
  image,
  imageData,
  sizes = 'min(96vw, 70rem)',
  previewWidth = RECOMMENDATION_IMAGE_PREVIEW_WIDTH,
  fullWidth = RECOMMENDATION_IMAGE_FULL_WIDTH
) {
  if (!(image instanceof HTMLImageElement) || !imageData) return;

  const previewSrc = imageData.previewSrc || imageData.src || '';
  const fullSrc = imageData.src || previewSrc;
  const nextSrc = previewSrc || fullSrc;
  const requestId = String(++modalImageSourceRequestId);

  image.dataset.hpModalSourceRequest = requestId;
  image.hidden = !nextSrc;
  if (image.hidden) {
    image.removeAttribute('src');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    delete image.dataset.hpModalPreviewSrc;
    delete image.dataset.hpModalFullSrc;
    return;
  }

  image.removeAttribute('srcset');
  image.removeAttribute('sizes');
  image.dataset.hpModalPreviewSrc = nextSrc;
  delete image.dataset.hpModalFullSrc;
  image.decoding = 'async';
  image.loading = 'eager';
  setImageFetchPriority(image, 'high');
  if (image.getAttribute('src') !== nextSrc) image.src = nextSrc;
  image.alt = imageData.alt || '';

  if (!fullSrc || fullSrc === nextSrc) return;

  preloadImageSrc(fullSrc).then(() => {
    if (image.dataset.hpModalSourceRequest !== requestId) return;

    image.srcset = `${nextSrc} ${previewWidth}w, ${fullSrc} ${fullWidth}w`;
    image.sizes = sizes;
    image.dataset.hpModalFullSrc = fullSrc;
  });
}

function preloadNeighborImages(images, index) {
  [index + 1, index - 1, index + 2].forEach((nextIndex) => {
    const image = images[nextIndex];
    if (!image) return;
    const previewSrc = image.previewSrc || image.src;
    const fullSrc = image.src || previewSrc;

    preloadImageSrc(previewSrc);
    if (fullSrc && fullSrc !== previewSrc) preloadImageSrc(fullSrc, 'low');
  });
}

function getProductGalleryImageData(gallery) {
  if (!(gallery instanceof HTMLElement)) return [];

  const containers = Array.from(
    gallery.querySelectorAll('.media-gallery__grid .product-media-container--image, slideshow-slide.product-media-container--image')
  );
  const seen = new Set();

  return containers.map(getProductImageDataFromContainer).filter((image) => {
    if (!image?.src || seen.has(image.key)) return false;
    seen.add(image.key);
    return true;
  });
}

function preloadProductGalleryImagesAround(gallery, index = 0, priority = 'auto') {
  const images = getProductGalleryImageData(gallery);
  if (!images.length) return;

  [index, index + 1, index - 1, index + 2].forEach((nextIndex) => {
    const image = images[nextIndex];
    if (!image) return;

    const previewSrc = image.previewSrc || image.src;
    const fullSrc = image.src || previewSrc;

    preloadImageSrc(previewSrc, priority);
    if (fullSrc && fullSrc !== previewSrc) preloadImageSrc(fullSrc, 'low');
  });
}

function bindProductGalleryImagePreloading(productInfo) {
  if (!(productInfo instanceof HTMLElement)) return;

  productInfo.querySelectorAll('.product-information__media media-gallery').forEach((gallery) => {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.hpImagePreloadBound === 'true') return;

    const scroller = gallery.querySelector('slideshow-slides');
    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    gallery.dataset.hpImagePreloadBound = 'true';

    const getCurrentIndex = () => {
      if (Number.isFinite(slideshow?.current)) return slideshow.current;
      if (scroller instanceof HTMLElement) {
        return Math.round(scroller.scrollLeft / Math.max(scroller.clientWidth, 1));
      }
      return 0;
    };

    const prime = (index = getCurrentIndex(), priority = 'auto') => {
      preloadProductGalleryImagesAround(gallery, Math.max(0, index), priority);
    };

    let preloadFrame = 0;
    const schedulePrime = () => {
      if (preloadFrame) return;

      preloadFrame = window.requestAnimationFrame(() => {
        preloadFrame = 0;
        prime();
      });
    };

    requestProductIdleCallback(() => prime(0));
    gallery.addEventListener('pointerenter', () => prime(), { passive: true });
    gallery.addEventListener('focusin', () => prime());
    scroller?.addEventListener?.('touchstart', () => prime(getCurrentIndex(), 'high'), { passive: true });
    scroller?.addEventListener?.('pointerdown', () => prime(getCurrentIndex(), 'high'), { passive: true });
    scroller?.addEventListener?.('scroll', schedulePrime, { passive: true });
    slideshow?.addEventListener?.('slideshow:select', (event) => {
      const index = Number(event?.detail?.index);
      prime(Number.isFinite(index) ? index : getCurrentIndex(), 'high');
    });
  });
}

function getProductInfoRoots(root = document) {
  if (root instanceof HTMLElement && root.matches(PRODUCT_PAGE_SELECTOR)) return [root];
  return Array.from(root.querySelectorAll(PRODUCT_PAGE_SELECTOR));
}

function getProductMediaHeight(productInfo) {
  const mediaGallery = productInfo.querySelector('.product-information__media media-gallery');
  const mediaGrid = mediaGallery?.querySelector('.media-gallery__grid');
  const mediaElement = mediaGrid || mediaGallery || productInfo.querySelector('.product-information__media');

  if (!(mediaElement instanceof HTMLElement)) return 0;

  return Math.round(mediaElement.getBoundingClientRect().height);
}

function resetProductDetailHeight(productInfo) {
  const detailCard = productInfo.querySelector('.product-details > .group-block');
  productInfo.style.removeProperty('--hp-product-panel-match-height');
  if (detailCard instanceof HTMLElement) detailCard.style.removeProperty('--hp-product-detail-match-height');
}

function syncProductDetailHeight(productInfo) {
  if (!(productInfo instanceof HTMLElement)) return;

  if (!productDesktopMedia.matches) {
    resetProductDetailHeight(productInfo);
    return;
  }

  const detailCard = productInfo.querySelector('.product-details > .group-block');
  if (!(detailCard instanceof HTMLElement)) return;

  productInfo.style.removeProperty('--hp-product-panel-match-height');
  detailCard.style.removeProperty('--hp-product-detail-match-height');

  const mediaHeight = getProductMediaHeight(productInfo);
  const detailHeight = Math.round(detailCard.scrollHeight || detailCard.getBoundingClientRect().height || 0);
  const matchHeight = Math.max(mediaHeight, detailHeight);
  if (matchHeight < 320) {
    resetProductDetailHeight(productInfo);
    return;
  }

  productInfo.style.setProperty('--hp-product-panel-match-height', `${matchHeight}px`);
  detailCard.style.setProperty('--hp-product-detail-match-height', `${matchHeight}px`);
}

function scheduleProductDetailHeightSync(productInfo) {
  if (!(productInfo instanceof HTMLElement)) return;

  productHeightSyncQueue.add(productInfo);
  if (productHeightSyncFrame) return;

  productHeightSyncFrame = window.requestAnimationFrame(() => {
    productHeightSyncFrame = 0;
    const queuedProductInfos = Array.from(productHeightSyncQueue);
    productHeightSyncQueue.clear();
    queuedProductInfos.forEach(syncProductDetailHeight);
  });
}

function bindProductDetailHeightSync(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpDetailHeightSyncBound === 'true') return;

  productInfo.dataset.hpDetailHeightSyncBound = 'true';
  const scheduleSync = () => scheduleProductDetailHeightSync(productInfo);

  scheduleSync();
  [80, 240, 720].forEach((delay) => window.setTimeout(scheduleSync, delay));
  window.addEventListener('resize', scheduleSync, { passive: true });
  window.addEventListener('load', scheduleSync, { passive: true });

  if (typeof productDesktopMedia.addEventListener === 'function') {
    productDesktopMedia.addEventListener('change', scheduleSync);
  }

  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(scheduleSync);
    [
      productInfo.querySelector('.product-information__media media-gallery'),
      productInfo.querySelector('.product-information__media .media-gallery__grid'),
      productInfo.querySelector('.product-details > .group-block'),
    ].forEach((element) => {
      if (element instanceof HTMLElement) resizeObserver.observe(element);
    });
    productInfo.hpDetailHeightResizeObserver = resizeObserver;
  }

  productInfo.querySelectorAll('.product-information__media img').forEach((image) => {
    image.addEventListener('load', scheduleSync, { once: true });
  });
}

function enhanceRecommendationQuickViewButtons(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
  scope.querySelectorAll(`${PRODUCT_RECOMMENDATIONS_SELECTOR} product-card`).forEach((card) => {
    if (!(card instanceof HTMLElement) || card.dataset.hpRecommendationQuickReady === 'true') return;

    const gallery = card.querySelector('.card-gallery');
    const productUrl = getRecommendationProductUrl(card);
    if (!(gallery instanceof HTMLElement) || !productUrl) return;

    card.dataset.hpRecommendationQuickReady = 'true';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hp-product-card__quick hp-product-recommendation__quick';
    button.setAttribute('data-hp-recommendation-open', '');
    button.setAttribute('aria-label', 'Open product popup');
    button.innerHTML = `
      <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="6.25"></circle>
        <path d="m16 16 4.25 4.25"></path>
      </svg>
    `;
    button.addEventListener('pointerenter', () => primeRecommendationQuickViewProduct(card), { passive: true });
    button.addEventListener('focus', () => primeRecommendationQuickViewProduct(card));
    gallery.append(button);
  });
  scheduleRecommendationQuickViewPrefetch(scope);
}

function bindRecommendationQuickView() {
  if (recommendationQuickViewBound) return;

  recommendationQuickViewBound = true;
  document.addEventListener('click', handleRecommendationQuickViewClick);

  if (!productRecommendationsObserver && document.body) {
    productRecommendationsObserver = new MutationObserver(() => enhanceRecommendationQuickViewButtons(document));
    productRecommendationsObserver.observe(document.body, { childList: true, subtree: true });
  }
}

function scheduleRecommendationQuickViewPrefetch(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
  const sections = scope.matches?.(PRODUCT_RECOMMENDATIONS_SELECTOR)
    ? [scope]
    : Array.from(scope.querySelectorAll(PRODUCT_RECOMMENDATIONS_SELECTOR));

  sections.forEach((section) => {
    if (!(section instanceof HTMLElement) || section.dataset.hpRecommendationPrefetchReady === 'true') return;

    section.dataset.hpRecommendationPrefetchReady = 'true';
    if (typeof IntersectionObserver === 'function') {
      recommendationPrefetchObserver ||= new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            recommendationPrefetchObserver?.unobserve(entry.target);
            prefetchRecommendationQuickViewProducts(entry.target);
          });
        },
        { rootMargin: '700px 0px' }
      );
      recommendationPrefetchObserver.observe(section);
      return;
    }

    prefetchRecommendationQuickViewProducts(section);
  });
}

function prefetchRecommendationQuickViewProducts(section) {
  requestProductIdleCallback(() => {
    Array.from(section.querySelectorAll('product-card')).slice(0, 5).forEach((card) => {
      if (card instanceof HTMLElement) primeRecommendationQuickViewProduct(card);
    });
  });
}

function primeRecommendationQuickViewProduct(card) {
  const productPath = getRecommendationProductUrl(card);
  if (!productPath) return;

  loadRecommendationProduct(productPath)
    .then((product) => {
      const firstImage = getRecommendationQuickViewImages(product)[0];
      preloadImageSrc(firstImage?.previewSrc || firstImage?.src || '');
    })
    .catch(() => {});
}

async function handleRecommendationQuickViewClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const modal = recommendationQuickViewState.modal;
  const close = target.closest('[data-hp-recommendation-close]');
  if (close) {
    event.preventDefault();
    closeRecommendationQuickView();
    return;
  }

  const panelToggle = target.closest('[data-hp-recommendation-panel-toggle]');
  if (panelToggle) {
    event.preventDefault();
    setRecommendationQuickViewPanel(panelToggle.getAttribute('data-panel') || 'images');
    return;
  }

  const prev = target.closest('[data-hp-recommendation-prev]');
  if (prev) {
    event.preventDefault();
    showRecommendationQuickViewImage(recommendationQuickViewState.activeImageIndex - 1);
    return;
  }

  const next = target.closest('[data-hp-recommendation-next]');
  if (next) {
    event.preventDefault();
    showRecommendationQuickViewImage(recommendationQuickViewState.activeImageIndex + 1);
    return;
  }

  const dot = target.closest('[data-hp-recommendation-dot]');
  if (dot) {
    event.preventDefault();
    showRecommendationQuickViewImage(Number(dot.getAttribute('data-index') || 0));
    return;
  }

  if (modal?.classList.contains('is-open') && !target.closest('.hp-product-modal__content')) {
    event.preventDefault();
    closeRecommendationQuickView();
    return;
  }

  const opener = target.closest('[data-hp-recommendation-open]');
  if (!opener) return;

  const card = opener.closest('product-card');
  if (!(card instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();
  await openRecommendationQuickView(card, opener);
}

function getRecommendationProductUrl(card) {
  const link = card?.querySelector('.product-card__link[href], a[href*="/products/"]');
  if (!(link instanceof HTMLAnchorElement)) return '';

  const url = new URL(link.getAttribute('href') || '', window.location.origin);
  url.search = '';
  url.hash = '';
  return url.pathname;
}

async function openRecommendationQuickView(card, opener) {
  const productPath = getRecommendationProductUrl(card);
  if (!productPath) return;

  const button = opener instanceof HTMLElement ? opener : null;
  button?.setAttribute('aria-busy', 'true');

  try {
    const product = await loadRecommendationProduct(productPath);
    renderRecommendationQuickView(product);
    recommendationQuickViewState.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    recommendationQuickViewState.activeProduct = product;
    recommendationQuickViewState.activeImageIndex = 0;
    setRecommendationQuickViewPanel('images');
    showRecommendationQuickViewImage(0);
    lockRecommendationQuickViewScroll();

    const modal = ensureRecommendationQuickViewModal();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.querySelector('.hp-product-modal__close')?.focus?.({ preventScroll: true });
  } catch (error) {
    console.error('Product popup could not open:', error);
    window.location.href = productPath;
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

async function loadRecommendationProduct(productPath) {
  if (productQuickViewCache.has(productPath)) return productQuickViewCache.get(productPath);

  const request = fetch(`${productPath}.js`, { headers: { Accept: 'application/json' } })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

      const rawProduct = await response.json();
      return mapShopifyProductForQuickView(rawProduct, productPath);
    })
    .catch((error) => {
      productQuickViewCache.delete(productPath);
      throw error;
    });

  productQuickViewCache.set(productPath, request);
  return request;
}

function mapShopifyProductForQuickView(product, productPath) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const prices = variants.map((variant) => Number(variant.price || 0)).filter((price) => Number.isFinite(price) && price > 0);
  const fallbackPrice = Number(product?.price || 0);
  const priceMin = Number(product?.price_min || Math.min(...prices, fallbackPrice) || fallbackPrice || 0);
  const priceMax = Number(product?.price_max || Math.max(...prices, fallbackPrice) || priceMin || 0);
  const rawImages = Array.isArray(product?.images) ? product.images : [];
  const featuredImage = normalizeRecommendationImageSrc(product?.featured_image || rawImages[0] || '');

  return {
    id: product?.id,
    handle: product?.handle || productPath.split('/').filter(Boolean).pop(),
    url: productPath,
    title: product?.title || '',
    vendor: product?.vendor || '',
    type: product?.type || '',
    priceMin,
    priceMax,
    featuredImage: getSizedShopifyImageSrc(featuredImage, RECOMMENDATION_IMAGE_FULL_WIDTH),
    images: rawImages.slice(0, 8).map((image) => {
      const normalizedImage = normalizeRecommendationImageSrc(image);
      return {
        src: getSizedShopifyImageSrc(normalizedImage, RECOMMENDATION_IMAGE_FULL_WIDTH),
        previewSrc: getSizedShopifyImageSrc(normalizedImage, RECOMMENDATION_IMAGE_PREVIEW_WIDTH),
        alt: product?.title || '',
      };
    }).filter((image) => image.src),
    descriptionHtml: product?.description || '<p>No description available.</p>',
  };
}

function normalizeRecommendationImageSrc(image) {
  const src = typeof image === 'string' ? image : image?.src || image?.url || '';
  if (!src) return '';
  if (src.startsWith('//')) return `${window.location.protocol}${src}`;
  return src;
}

function ensureRecommendationQuickViewModal() {
  if (recommendationQuickViewState.modal instanceof HTMLElement) return recommendationQuickViewState.modal;

  const modal = document.createElement('div');
  modal.className = 'hp-product-modal hp-product-modal--recommendations';
  modal.setAttribute('data-hp-modal-view', 'images');
  modal.setAttribute('data-hp-recommendation-modal', '');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <button type="button" class="hp-product-modal__backdrop" data-hp-recommendation-close aria-label="Close product details"></button>
    <div class="hp-product-modal__content" role="dialog" aria-modal="true" aria-labelledby="hp-recommendation-modal-title">
      <button type="button" class="hp-product-modal__close" data-hp-recommendation-close aria-label="Close product details">&times;</button>
      <div class="hp-product-modal__switch" role="tablist" aria-label="Product popup view">
        <button type="button" class="hp-product-modal__switch-button is-active" data-hp-recommendation-panel-toggle data-panel="images" role="tab" aria-selected="true">Images</button>
        <button type="button" class="hp-product-modal__switch-button" data-hp-recommendation-panel-toggle data-panel="details" role="tab" aria-selected="false">Details</button>
      </div>
      <div class="hp-product-modal__layout">
        <section class="hp-product-modal__gallery" data-hp-modal-panel="images" aria-label="Product images">
          <div class="hp-product-modal__media">
            <button type="button" class="hp-product-modal__nav hp-product-modal__nav--prev" data-hp-recommendation-prev aria-label="Previous product image" hidden>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>
            </button>
            <img src="" alt="" class="hp-product-modal__image" data-hp-recommendation-image width="1600" height="1600" loading="eager" decoding="async" fetchpriority="high">
            <button type="button" class="hp-product-modal__nav hp-product-modal__nav--next" data-hp-recommendation-next aria-label="Next product image" hidden>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>
            </button>
          </div>
          <div class="hp-product-modal__dots" data-hp-recommendation-dots hidden></div>
        </section>
        <section class="hp-product-modal__details" data-hp-modal-panel="details" aria-label="Product details">
          <p class="hp-product-modal__kicker" data-hp-recommendation-vendor></p>
          <h2 class="hp-product-modal__title" id="hp-recommendation-modal-title" data-hp-recommendation-title></h2>
          <p class="hp-product-modal__price" data-hp-recommendation-price></p>
          <div class="hp-product-modal__description rte" data-hp-recommendation-description></div>
          <a href="#" class="hp-product-modal__link" data-hp-recommendation-link>View product page</a>
        </section>
      </div>
    </div>
  `;
  document.body.append(modal);
  recommendationQuickViewState.modal = modal;
  bindRecommendationQuickViewSwipe(modal);
  return modal;
}

function renderRecommendationQuickView(product) {
  const modal = ensureRecommendationQuickViewModal();
  const images = getRecommendationQuickViewImages(product);

  modal.querySelector('[data-hp-recommendation-title]').textContent = product.title || '';
  modal.querySelector('[data-hp-recommendation-vendor]').textContent = [product.vendor, product.type].filter(Boolean).join(' / ');
  modal.querySelector('[data-hp-recommendation-price]').textContent = formatRecommendationProductPrice(product);
  modal.querySelector('[data-hp-recommendation-description]').innerHTML = product.descriptionHtml || '<p>No description available.</p>';
  modal.querySelector('[data-hp-recommendation-link]').href = product.url || '#';

  const dots = modal.querySelector('[data-hp-recommendation-dots]');
  if (dots instanceof HTMLElement) {
    dots.hidden = images.length <= 1;
    dots.innerHTML = images.map((_, index) => (
      `<button type="button" class="hp-product-modal__dot" data-hp-recommendation-dot data-index="${index}" aria-label="Show image ${index + 1}"></button>`
    )).join('');
  }
}

function setRecommendationQuickViewPanel(panel = 'images') {
  const modal = ensureRecommendationQuickViewModal();
  const nextPanel = panel === 'details' ? 'details' : 'images';
  modal.setAttribute('data-hp-modal-view', nextPanel);
  modal.querySelectorAll('[data-hp-recommendation-panel-toggle]').forEach((button) => {
    const isActive = button.getAttribute('data-panel') === nextPanel;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  modal.querySelectorAll('.hp-product-modal__content, .hp-product-modal__details, .hp-product-modal__description').forEach((element) => {
    if (element instanceof HTMLElement) element.scrollTop = 0;
  });
}

function getRecommendationQuickViewImages(product) {
  const images = Array.isArray(product?.images) ? product.images.filter((image) => image.src || image.previewSrc) : [];
  if (images.length) return images;
  if (product?.featuredImage) return [{ src: product.featuredImage, previewSrc: product.featuredImage, alt: product.title }];
  return [];
}

function showRecommendationQuickViewImage(index) {
  const modal = ensureRecommendationQuickViewModal();
  const product = recommendationQuickViewState.activeProduct;
  const images = getRecommendationQuickViewImages(product);
  const image = modal.querySelector('[data-hp-recommendation-image]');
  const prev = modal.querySelector('[data-hp-recommendation-prev]');
  const next = modal.querySelector('[data-hp-recommendation-next]');

  if (!(image instanceof HTMLImageElement)) return;
  if (!images.length) {
    image.hidden = true;
    image.removeAttribute('src');
    return;
  }

  const nextIndex = Math.max(0, Math.min(images.length - 1, index));
  const nextImage = images[nextIndex];
  recommendationQuickViewState.activeImageIndex = nextIndex;

  setModalImageSource(image, { ...nextImage, alt: nextImage.alt || product?.title || '' });
  preloadNeighborImages(images, nextIndex);

  if (prev instanceof HTMLButtonElement) {
    prev.hidden = images.length <= 1;
    prev.disabled = nextIndex <= 0;
    prev.classList.toggle('is-muted', nextIndex <= 0);
  }
  if (next instanceof HTMLButtonElement) {
    next.hidden = images.length <= 1;
    next.disabled = nextIndex >= images.length - 1;
    next.classList.toggle('is-muted', nextIndex >= images.length - 1);
  }
  modal.querySelectorAll('[data-hp-recommendation-dot]').forEach((dot) => {
    dot.classList.toggle('is-active', Number(dot.getAttribute('data-index')) === nextIndex);
  });
}

function bindRecommendationQuickViewSwipe(modal) {
  const startSwipe = (event) => {
    if (!modal.classList.contains('is-open') || modal.getAttribute('data-hp-modal-view') !== 'images') return;
    if (event.target instanceof Element && event.target.closest('.hp-product-modal__nav, .hp-product-modal__dot')) return;
    if (getRecommendationQuickViewImages(recommendationQuickViewState.activeProduct).length <= 1) return;
    recommendationQuickViewState.swipe = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      swiping: false,
    };
    event.currentTarget?.setPointerCapture?.(event.pointerId);
  };

  const trackSwipe = (event) => {
    const swipe = recommendationQuickViewState.swipe;
    if (!swipe || swipe.pointerId !== event.pointerId) return;

    swipe.lastX = event.clientX;
    swipe.lastY = event.clientY;
    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    if (!swipe.swiping && Math.abs(deltaX) > 14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) swipe.swiping = true;
    if (swipe.swiping) event.preventDefault();
  };

  const finishSwipe = (event) => {
    const swipe = recommendationQuickViewState.swipe;
    if (!swipe || swipe.pointerId !== event.pointerId) return;

    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    recommendationQuickViewState.swipe = null;
    event.currentTarget?.releasePointerCapture?.(event.pointerId);

    const minimumSwipe = Math.max(34, Math.min(58, window.innerWidth * .08));
    if (Math.abs(deltaX) < minimumSwipe || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
    showRecommendationQuickViewImage(recommendationQuickViewState.activeImageIndex + (deltaX < 0 ? 1 : -1));
  };

  modal.querySelectorAll('.hp-product-modal__gallery, .hp-product-modal__media').forEach((surface) => {
    surface.addEventListener('pointerdown', startSwipe);
    surface.addEventListener('pointermove', trackSwipe, { passive: false });
    surface.addEventListener('pointerup', finishSwipe);
    surface.addEventListener('pointercancel', () => {
      recommendationQuickViewState.swipe = null;
    });
    surface.addEventListener('dragstart', (event) => event.preventDefault());
  });
}

function closeRecommendationQuickView() {
  const modal = recommendationQuickViewState.modal;
  if (!modal) return;

  const wasOpen = modal.classList.contains('is-open') || modal.getAttribute('aria-hidden') === 'false';
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  recommendationQuickViewState.activeProduct = null;
  recommendationQuickViewState.activeImageIndex = 0;
  recommendationQuickViewState.swipe = null;
  if (wasOpen) unlockRecommendationQuickViewScroll();
  if (wasOpen) recommendationQuickViewState.lastFocusedElement?.focus?.({ preventScroll: true });
}

function getModalScrollbarCompensation() {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

function applyModalScrollLock(state) {
  state.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
  state.previousDocumentOverflow = document.documentElement.style.overflow;
  state.previousDocumentOverscrollBehavior = document.documentElement.style.overscrollBehavior;
  state.previousDocumentScrollbarGutter = document.documentElement.style.scrollbarGutter;
  state.previousDocumentScrollBehavior = document.documentElement.style.scrollBehavior;
  state.previousBodyOverflow = document.body.style.overflow;
  state.previousBodyOverscrollBehavior = document.body.style.overscrollBehavior;
  state.previousBodyPaddingRight = document.body.style.paddingRight;
  state.previousBodyPosition = document.body.style.position;
  state.previousBodyTop = document.body.style.top;
  state.previousBodyLeft = document.body.style.left;
  state.previousBodyRight = document.body.style.right;
  state.previousBodyWidth = document.body.style.width;
  state.previousBodyScrollBehavior = document.body.style.scrollBehavior;

  const scrollbarWidth = getModalScrollbarCompensation();
  const bodyPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
  const supportsStableScrollbarGutter = window.CSS?.supports?.('scrollbar-gutter: stable') === true;

  document.documentElement.style.scrollbarGutter = 'stable';
  document.documentElement.style.overflow = 'hidden';
  document.documentElement.style.overscrollBehavior = 'none';
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.overflow = 'hidden';
  document.body.style.overscrollBehavior = 'none';
  document.body.style.scrollBehavior = 'auto';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${state.scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  if (scrollbarWidth > 0 && !supportsStableScrollbarGutter) {
    document.body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
  }
}

function releaseModalScrollLock(state) {
  const lockedBodyTop = Number.parseFloat(document.body.style.top || '');
  const nextScrollY = Math.max(
    0,
    Number.isFinite(state.scrollY) && state.scrollY > 0
      ? state.scrollY
      : Number.isFinite(lockedBodyTop)
        ? Math.abs(lockedBodyTop)
        : 0
  );
  const restoreScrollPosition = () => {
    window.scrollTo(0, nextScrollY);
    if (document.scrollingElement) document.scrollingElement.scrollTop = nextScrollY;
    document.documentElement.scrollTop = nextScrollY;
    document.body.scrollTop = nextScrollY;
  };
  const restoreScrollBehavior = () => {
    document.documentElement.style.scrollBehavior = state.previousDocumentScrollBehavior || '';
    document.body.style.scrollBehavior = state.previousBodyScrollBehavior || '';
  };
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.scrollBehavior = 'auto';
  document.documentElement.style.overflow = state.previousDocumentOverflow || '';
  document.documentElement.style.overscrollBehavior = state.previousDocumentOverscrollBehavior || '';
  document.documentElement.style.scrollbarGutter = state.previousDocumentScrollbarGutter || '';
  document.body.style.overflow = state.previousBodyOverflow || '';
  document.body.style.overscrollBehavior = state.previousBodyOverscrollBehavior || '';
  document.body.style.paddingRight = state.previousBodyPaddingRight || '';
  document.body.style.position = state.previousBodyPosition || '';
  document.body.style.top = state.previousBodyTop || '';
  document.body.style.left = state.previousBodyLeft || '';
  document.body.style.right = state.previousBodyRight || '';
  document.body.style.width = state.previousBodyWidth || '';
  restoreScrollPosition();
  window.requestAnimationFrame(restoreScrollPosition);
  window.setTimeout(restoreScrollPosition, 80);
  window.setTimeout(restoreScrollPosition, 260);
  window.setTimeout(restoreScrollBehavior, 320);
}

function lockRecommendationQuickViewScroll() {
  applyModalScrollLock(recommendationQuickViewState);
}

function unlockRecommendationQuickViewScroll() {
  releaseModalScrollLock(recommendationQuickViewState);
}

function bindProductImageModal() {
  if (productImageModalBound) return;

  productImageModalBound = true;
  document.addEventListener('click', handleProductImageModalClick);
  document.addEventListener('keydown', handleProductImageModalKeydown);
}

function handleProductImageModalClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const close = target.closest('[data-hp-product-image-close]');
  if (close) {
    event.preventDefault();
    closeProductImageModal();
    return;
  }

  const prev = target.closest('[data-hp-product-image-prev]');
  if (prev) {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex - 1);
    return;
  }

  const next = target.closest('[data-hp-product-image-next]');
  if (next) {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex + 1);
    return;
  }

  const dot = target.closest('[data-hp-product-image-dot]');
  if (dot) {
    event.preventDefault();
    showProductImageModalImage(Number(dot.getAttribute('data-index') || 0));
    return;
  }

  const modal = productImageModalState.modal;
  if (modal?.classList.contains('is-open') && target.closest('[data-hp-product-image-modal]') && !target.closest('.hp-product-modal__content')) {
    event.preventDefault();
    closeProductImageModal();
    return;
  }

  if (target.closest('[data-hp-product-image-modal]')) return;

  const productInfo = target.closest(PRODUCT_PAGE_SELECTOR);
  const imageContainer = target.closest('.product-information__media .product-media-container--image');
  if (!(productInfo instanceof HTMLElement) || !(imageContainer instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();
  openProductImageModal(imageContainer, target);
}

function handleProductImageModalKeydown(event) {
  const modal = productImageModalState.modal;
  if (!modal?.classList.contains('is-open')) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    closeProductImageModal();
  }
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex - 1);
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex + 1);
  }
}

function openProductImageModal(container, opener) {
  const images = getProductGalleryImages(container);
  if (!images.length) return;

  const modal = ensureProductImageModal();
  const activeIndex = Math.max(0, getProductImageIndex(container, images));
  productImageModalState.images = images;
  productImageModalState.activeImageIndex = activeIndex;
  productImageModalState.lastFocusedElement = opener instanceof HTMLElement ? opener : document.activeElement instanceof HTMLElement ? document.activeElement : null;
  productImageModalState.swipe = null;

  renderProductImageModalDots(images);
  showProductImageModalImage(activeIndex);
  lockProductImageModalScroll();
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  modal.querySelector('.hp-product-modal__close')?.focus?.({ preventScroll: true });
}

function getProductGalleryImages(activeContainer) {
  const gallery = activeContainer.closest('media-gallery');
  if (!(gallery instanceof HTMLElement)) return [];

  const containers = Array.from(
    gallery.querySelectorAll('.media-gallery__grid .product-media-container--image, slideshow-slide.product-media-container--image')
  );
  const seen = new Set();

  return containers.map(getProductImageDataFromContainer).filter((image) => {
    if (!image?.src || seen.has(image.key)) return false;
    seen.add(image.key);
    return true;
  });
}

function getProductImageDataFromContainer(container) {
  if (!(container instanceof HTMLElement)) return null;

  const image = container.querySelector('.product-media__image');
  if (!(image instanceof HTMLImageElement)) return null;

  const mediaId = container.querySelector('.product-media')?.getAttribute('data-media-id') || '';
  const fullSource = image.dataset.maxResolution || image.currentSrc || image.src || '';
  const previewSource = image.currentSrc || image.src || fullSource;
  const src = getSizedShopifyImageSrc(fullSource, PRODUCT_IMAGE_FULL_WIDTH);
  const previewSrc = getSizedShopifyImageSrc(previewSource, PRODUCT_IMAGE_PREVIEW_WIDTH);

  return {
    key: mediaId || src || previewSrc,
    src,
    previewSrc,
    alt: image.alt || '',
  };
}

function getProductImageIndex(container, images) {
  const activeImage = getProductImageDataFromContainer(container);
  const activeIndex = images.findIndex((image) => image.key === activeImage?.key);
  return activeIndex >= 0 ? activeIndex : 0;
}

function ensureProductImageModal() {
  if (productImageModalState.modal instanceof HTMLElement) return productImageModalState.modal;

  const modal = document.createElement('div');
  modal.className = 'hp-product-modal hp-product-modal--image';
  modal.setAttribute('data-hp-product-image-modal', '');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <button type="button" class="hp-product-modal__backdrop" data-hp-product-image-close aria-label="Close product image"></button>
    <div class="hp-product-modal__content" role="dialog" aria-modal="true" aria-label="Product image viewer">
      <button type="button" class="hp-product-modal__close" data-hp-product-image-close aria-label="Close product image">&times;</button>
      <section class="hp-product-modal__gallery" aria-label="Product images">
        <div class="hp-product-modal__media">
          <button type="button" class="hp-product-modal__nav hp-product-modal__nav--prev" data-hp-product-image-prev aria-label="Previous product image" hidden>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>
          </button>
          <img src="" alt="" class="hp-product-modal__image" data-hp-product-image width="1600" height="1600" loading="eager" decoding="async" fetchpriority="high">
          <button type="button" class="hp-product-modal__nav hp-product-modal__nav--next" data-hp-product-image-next aria-label="Next product image" hidden>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>
          </button>
        </div>
        <div class="hp-product-modal__dots" data-hp-product-image-dots hidden></div>
      </section>
    </div>
  `;
  document.body.append(modal);
  productImageModalState.modal = modal;
  bindProductImageModalSwipe(modal);
  return modal;
}

function renderProductImageModalDots(images) {
  const dots = ensureProductImageModal().querySelector('[data-hp-product-image-dots]');
  if (!(dots instanceof HTMLElement)) return;

  dots.hidden = images.length <= 1;
  dots.innerHTML = images.map((_, index) => (
    `<button type="button" class="hp-product-modal__dot" data-hp-product-image-dot data-index="${index}" aria-label="Show image ${index + 1}"></button>`
  )).join('');
}

function showProductImageModalImage(index) {
  const modal = ensureProductImageModal();
  const images = productImageModalState.images;
  const image = modal.querySelector('[data-hp-product-image]');

  if (!(image instanceof HTMLImageElement)) return;
  if (!images.length) {
    image.hidden = true;
    image.removeAttribute('src');
    image.removeAttribute('srcset');
    return;
  }

  const nextIndex = Math.max(0, Math.min(images.length - 1, index));
  const nextImage = images[nextIndex];
  productImageModalState.activeImageIndex = nextIndex;
  setModalImageSource(image, nextImage, 'min(96vw, 82rem)', PRODUCT_IMAGE_PREVIEW_WIDTH, PRODUCT_IMAGE_FULL_WIDTH);
  preloadNeighborImages(images, nextIndex);
  updateProductImageModalNavigationState(nextIndex);

  modal.querySelectorAll('[data-hp-product-image-dot]').forEach((dot) => {
    dot.classList.toggle('is-active', Number(dot.getAttribute('data-index')) === nextIndex);
  });
}

function updateProductImageModalNavigationState(index = productImageModalState.activeImageIndex) {
  const modal = productImageModalState.modal;
  const images = productImageModalState.images;
  const prev = modal?.querySelector('[data-hp-product-image-prev]');
  const next = modal?.querySelector('[data-hp-product-image-next]');
  const currentIndex = Math.max(0, Math.min(images.length - 1, index));

  if (prev instanceof HTMLButtonElement) {
    prev.hidden = images.length <= 1;
    prev.disabled = currentIndex <= 0;
    prev.classList.toggle('is-muted', currentIndex <= 0);
  }
  if (next instanceof HTMLButtonElement) {
    next.hidden = images.length <= 1;
    next.disabled = currentIndex >= images.length - 1;
    next.classList.toggle('is-muted', currentIndex >= images.length - 1);
  }
}

function bindProductImageModalSwipe(modal) {
  const startSwipe = (event) => {
    if (!modal.classList.contains('is-open')) return;
    if (event.target instanceof Element && event.target.closest('.hp-product-modal__nav, .hp-product-modal__dot')) return;
    if (productImageModalState.images.length <= 1) return;
    productImageModalState.swipe = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      swiping: false,
    };
    event.currentTarget?.setPointerCapture?.(event.pointerId);
  };

  const trackSwipe = (event) => {
    const swipe = productImageModalState.swipe;
    if (!swipe || swipe.pointerId !== event.pointerId) return;

    swipe.lastX = event.clientX;
    swipe.lastY = event.clientY;
    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    if (!swipe.swiping && Math.abs(deltaX) > 14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) swipe.swiping = true;
    if (swipe.swiping) {
      event.preventDefault();
      const previewSwipe = Math.max(18, Math.min(34, window.innerWidth * .045));
      const previewIndex = Math.abs(deltaX) >= previewSwipe
        ? productImageModalState.activeImageIndex + (deltaX < 0 ? 1 : -1)
        : productImageModalState.activeImageIndex;
      updateProductImageModalNavigationState(previewIndex);
    }
  };

  const finishSwipe = (event) => {
    const swipe = productImageModalState.swipe;
    if (!swipe || swipe.pointerId !== event.pointerId) return;

    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    productImageModalState.swipe = null;
    event.currentTarget?.releasePointerCapture?.(event.pointerId);

    const minimumSwipe = Math.max(34, Math.min(58, window.innerWidth * .08));
    if (Math.abs(deltaX) < minimumSwipe || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) {
      updateProductImageModalNavigationState();
      return;
    }
    showProductImageModalImage(productImageModalState.activeImageIndex + (deltaX < 0 ? 1 : -1));
  };

  modal.querySelectorAll('.hp-product-modal__gallery, .hp-product-modal__media').forEach((surface) => {
    surface.addEventListener('pointerdown', startSwipe);
    surface.addEventListener('pointermove', trackSwipe, { passive: false });
    surface.addEventListener('pointerup', finishSwipe);
    surface.addEventListener('pointercancel', () => {
      productImageModalState.swipe = null;
      updateProductImageModalNavigationState();
    });
    surface.addEventListener('dragstart', (event) => event.preventDefault());
  });
}

function closeProductImageModal() {
  const modal = productImageModalState.modal;
  if (!modal) return;

  const wasOpen = modal.classList.contains('is-open') || modal.getAttribute('aria-hidden') === 'false';
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  productImageModalState.images = [];
  productImageModalState.activeImageIndex = 0;
  productImageModalState.swipe = null;
  if (wasOpen) unlockProductImageModalScroll();
  if (wasOpen) productImageModalState.lastFocusedElement?.focus?.({ preventScroll: true });
}

function lockProductImageModalScroll() {
  applyModalScrollLock(productImageModalState);
}

function unlockProductImageModalScroll() {
  releaseModalScrollLock(productImageModalState);
}

function getProductHelpData(productInfo) {
  const sectionRoot = productInfo?.closest?.('.shopify-section') || productInfo?.parentElement || document;
  const data = sectionRoot.querySelector('[data-hp-product-help-data]');
  return data instanceof HTMLElement ? data : null;
}

function shouldPreventHelpDrawerPanelScroll(panel, deltaY) {
  if (!(panel instanceof HTMLElement)) return true;
  if (panel.scrollHeight <= panel.clientHeight + 1) return true;

  const atTop = panel.scrollTop <= 1;
  const atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;
  return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
}

function bindProductHelpDrawerScrollGuard(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.dataset.hpScrollGuardBound === 'true') return;

  drawer.dataset.hpScrollGuardBound = 'true';
  let lastTouchY = 0;
  const getPanel = (event) =>
    event.target instanceof Element ? event.target.closest('.hp-product-help-drawer__panel') : null;

  drawer.addEventListener(
    'touchstart',
    (event) => {
      lastTouchY = event.touches?.[0]?.clientY || 0;
    },
    { passive: true }
  );

  drawer.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches?.[0];
      const panel = getPanel(event);
      if (!touch || !(panel instanceof HTMLElement)) {
        event.preventDefault();
        return;
      }

      const deltaY = lastTouchY - touch.clientY;
      lastTouchY = touch.clientY;
      if (Math.abs(deltaY) < 1) return;
      if (shouldPreventHelpDrawerPanelScroll(panel, deltaY)) event.preventDefault();
    },
    { passive: false }
  );

  drawer.addEventListener(
    'wheel',
    (event) => {
      const panel = getPanel(event);
      if (!(panel instanceof HTMLElement) || shouldPreventHelpDrawerPanelScroll(panel, event.deltaY)) {
        event.preventDefault();
      }
    },
    { passive: false }
  );
}

function bindProductHelpTrigger(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpProductHelpReady === 'true') return;

  const data = getProductHelpData(productInfo);
  const builderTemplate = data?.querySelector('[data-hp-product-builder-trigger-template]');
  const triggerTemplate = data?.querySelector('[data-hp-product-help-trigger-template]');
  const content = productInfo.querySelector('.product-details .group-block-content');
  if (!(content instanceof HTMLElement)) return;
  if (!(builderTemplate instanceof HTMLTemplateElement) && !(triggerTemplate instanceof HTMLTemplateElement)) return;

  productInfo.dataset.hpProductHelpReady = 'true';
  if (
    (content.querySelector('[data-hp-product-builder-trigger]') || !(builderTemplate instanceof HTMLTemplateElement)) &&
    (content.querySelector('[data-hp-product-help-trigger]') || !(triggerTemplate instanceof HTMLTemplateElement))
  ) {
    return;
  }

  const trigger = triggerTemplate instanceof HTMLTemplateElement
    ? triggerTemplate.content.firstElementChild?.cloneNode(true)
    : null;
  const builderTrigger = builderTemplate instanceof HTMLTemplateElement
    ? builderTemplate.content.firstElementChild?.cloneNode(true)
    : null;

  const description = Array.from(content.children).find((child) =>
    child instanceof HTMLElement && (child.matches('rte-formatter.rte, .rte') || child.querySelector('rte-formatter.rte, .rte'))
  );
  const variantPicker = Array.from(content.children).find((child) => child instanceof HTMLElement && child.matches('variant-picker'));

  const insertTriggersBefore = (reference) => {
    if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]')) {
      reference.before(builderTrigger);
    }
    if (trigger instanceof HTMLElement && !content.querySelector('[data-hp-product-help-trigger]')) {
      reference.before(trigger);
    }
  };

  if (description instanceof HTMLElement) {
    let helpTriggerAnchor = content.querySelector('[data-hp-product-help-trigger]');
    if (trigger instanceof HTMLElement && !content.querySelector('[data-hp-product-help-trigger]')) {
      description.insertAdjacentElement('afterend', trigger);
      helpTriggerAnchor = trigger;
    }
    if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]') && helpTriggerAnchor instanceof HTMLElement) {
      helpTriggerAnchor.before(builderTrigger);
    } else if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]')) {
      description.insertAdjacentElement('afterend', builderTrigger);
    }
  } else if (variantPicker instanceof HTMLElement) {
    insertTriggersBefore(variantPicker);
  } else {
    if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]')) {
      content.append(builderTrigger);
    }
    if (trigger instanceof HTMLElement && !content.querySelector('[data-hp-product-help-trigger]')) {
      content.append(trigger);
    }
  }

  scheduleProductDetailHeightSync(productInfo);
}

function bindProductHelpDrawer() {
  if (productHelpDrawerBound) return;

  productHelpDrawerBound = true;
  document.addEventListener('click', handleProductHelpDrawerClick);
  document.addEventListener('keydown', handleProductHelpDrawerKeydown);
}

function handleProductHelpDrawerClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('[data-hp-product-help-close]')) {
    event.preventDefault();
    closeProductHelpDrawer();
    return;
  }

  const opener = target.closest('[data-hp-product-help-open]');
  if (!opener) return;

  const productInfo = opener.closest(PRODUCT_PAGE_SELECTOR) || document.querySelector(PRODUCT_PAGE_SELECTOR);
  if (!(productInfo instanceof HTMLElement)) return;

  event.preventDefault();
  openProductHelpDrawer(productInfo, opener);
}

function handleProductHelpDrawerKeydown(event) {
  const drawer = productHelpDrawerState.modal;
  if (!drawer?.classList.contains('is-open')) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    closeProductHelpDrawer();
    return;
  }

  if (event.key !== 'Tab') return;

  const panel = drawer.querySelector('.hp-product-help-drawer__panel');
  if (!(panel instanceof HTMLElement)) return;

  const focusableElements = getProductHelpDrawerFocusableElements(panel);
  if (!focusableElements.length) {
    event.preventDefault();
    panel.focus?.({ preventScroll: true });
    return;
  }

  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function getProductHelpDrawerFocusableElements(panel) {
  return Array.from(
    panel.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')
  ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
}

function ensureProductHelpDrawer(productInfo) {
  if (productHelpDrawerState.modal instanceof HTMLElement) return productHelpDrawerState.modal;

  const data = getProductHelpData(productInfo);
  const drawerTemplate = data?.querySelector('[data-hp-product-help-drawer-template]');
  if (!(drawerTemplate instanceof HTMLTemplateElement)) return null;

  const drawer = drawerTemplate.content.firstElementChild?.cloneNode(true);
  if (!(drawer instanceof HTMLElement)) return null;

  document.body.append(drawer);
  bindProductHelpDrawerScrollGuard(drawer);
  productHelpDrawerState.modal = drawer;
  return drawer;
}

function openProductHelpDrawer(productInfo, opener) {
  const drawer = ensureProductHelpDrawer(productInfo);
  if (!(drawer instanceof HTMLElement)) return;

  productHelpDrawerState.lastFocusedElement =
    opener instanceof HTMLElement ? opener : document.activeElement instanceof HTMLElement ? document.activeElement : null;

  if (!drawer.classList.contains('is-open')) applyModalScrollLock(productHelpDrawerState);
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');

  const closeButton = drawer.querySelector('.hp-product-help-drawer__close');
  if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
}

function closeProductHelpDrawer() {
  const drawer = productHelpDrawerState.modal;
  if (!(drawer instanceof HTMLElement)) return;

  const wasOpen = drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') === 'false';
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (wasOpen) releaseModalScrollLock(productHelpDrawerState);
  if (wasOpen) productHelpDrawerState.lastFocusedElement?.focus?.({ preventScroll: true });
}

function formatRecommendationProductPrice(product) {
  const min = Number(product?.priceMin || 0);
  const max = Number(product?.priceMax || min);
  if (max > min) return `From ${formatRecommendationMoney(min)}`;
  return formatRecommendationMoney(min);
}

function formatRecommendationMoney(cents) {
  const amount = Number(cents || 0) / 100;
  const currency = window.Shopify?.currency?.active || 'SGD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
}

function initRecommendationQuickView(root = document) {
  enhanceRecommendationQuickViewButtons(root);
  bindRecommendationQuickView();
}

function initProductPageHelpers(root = document) {
  const productInfos = getProductInfoRoots(root);

  productInfos.forEach((productInfo) => {
    bindProductGalleryLayoutEnhancements(productInfo);
    bindProductGalleryMobileArrows(productInfo);
    bindProductVariantGalleryStability(productInfo);
    bindProductGalleryImagePreloading(productInfo);
    bindProductDetailHeightSync(productInfo);
    bindProductHelpTrigger(productInfo);

    productInfo.querySelectorAll('.product-details rte-formatter.rte, .product-details .rte').forEach(bindDescriptionScrollRelease);
    productInfo.querySelectorAll('media-gallery slideshow-slides').forEach(bindGalleryEdgeGuard);
  });

  bindProductImageModal();
  bindProductHelpDrawer();
  initRecommendationQuickView(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductPageHelpers(), { once: true });
} else {
  initProductPageHelpers();
}

document.addEventListener('shopify:section:load', (event) => {
  initProductPageHelpers(event.target);
});
